import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { jwtDecode } from 'jwt-decode'
import { login as loginApi, getCurrentUser, logout as logoutApi, refreshToken as refreshTokenApi, type User, type DeviceInfo } from '@/api/user'
import { USER_INFO_KEY } from '@/constants/storage'

export interface UserInfo {
  id: string
  username: string
  email: string
  nickname?: string
  avatar?: string
  role: string
  status: string
  ipLocation?: string
}

// 将 API User 转换为 UserInfo
const convertUser = (user: User): UserInfo => ({
  id: user.id,
  username: user.username,
  email: user.email || '',
  nickname: user.nickname,
  avatar: user.avatar,
  role: user.role,
  status: 'active',
  ipLocation: user.ip_location
})

/**
 * 解析 JWT token 获取过期时间
 * @param token JWT token
 * @returns 过期时间戳（秒），解析失败返回 null
 */
const parseTokenExp = (token: string): number | null => {
  try {
    const payload = jwtDecode<{ exp: number }>(token)
    return payload.exp || null
  } catch (err) {
    console.error('[Token] 解析失败:', err)
    return null
  }
}

/**
 * 检查 token 是否即将过期
 * @param token JWT token
 * @param threshold 提前阈值（秒），默认 5 分钟
 * @returns 是否即将过期
 */
const isTokenExpiringSoon = (token: string, threshold: number = 300): boolean => {
  const exp = parseTokenExp(token)
  if (!exp) return false
  const now = Math.floor(Date.now() / 1000)
 
  return exp - now < threshold
}

export const useUserStore = defineStore('user', () => {
  // State
  const accessToken = ref<string>('')
  const refreshToken = ref<string>('')
  const userInfo = ref<UserInfo | null>(null)
  const tokenIssuedAt = ref<number>(0) // token 获取时间戳

  // Getters
  const isLoggedIn = computed(() => !!accessToken.value)
  const userId = computed(() => userInfo.value?.id || '')
  const username = computed(() => userInfo.value?.nickname || userInfo.value?.username || '')
  const avatar = computed(() => userInfo.value?.avatar || '')
  const role = computed(() => userInfo.value?.role || '')
  const isAdmin = computed(() => userInfo.value?.role === 'admin')
  const token = computed(() => accessToken.value)

  // Actions
  const setToken = (newAccessToken: string, newRefreshToken?: string) => {
    accessToken.value = newAccessToken
    if (newRefreshToken) {
      refreshToken.value = newRefreshToken
    }
    // 记录 token 获取时间
    tokenIssuedAt.value = Math.floor(Date.now() / 1000)
  }

  const getToken = () => accessToken.value

  const getRefreshToken = () => refreshToken.value

  const clearToken = () => {
    accessToken.value = ''
    refreshToken.value = ''
    userInfo.value = null
    tokenIssuedAt.value = 0
  }

  const setUserInfo = (user: UserInfo | null) => {
    userInfo.value = user
  }

  const login = async (
    username: string,
    password: string,
    captchaId?: string,
    slideX?: number,
    points?: { x: number; y: number }[],
    deviceInfo?: DeviceInfo
  ) => {
    try {
      const loginData: any = { username, password }

      if (captchaId) {
        loginData.captchaId = captchaId
        if (slideX && slideX > 0) loginData.slideX = slideX
        if (points?.length) loginData.points = points
      }

      if (deviceInfo) {
        loginData.device_info = deviceInfo
      }

      const data = await loginApi(loginData)

      const tokenVal = data?.access_token
      const refreshVal = data?.refresh_token
      if (!tokenVal) {
        return { success: false, message: '登录响应缺少 token' }
      }

      setToken(tokenVal, refreshVal)
      if (data?.user) setUserInfo(convertUser(data.user))

      return { success: true, data }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || '登录失败，请检查网络连接'
      }
    }
  }
  
  const refreshAccessToken = async () => {
    try {
      // Web 端：refresh_token 会自动从 HttpOnly Cookie 中读取
      // App/小程序端：从本地存储获取 refresh_token
      const refreshVal = getRefreshToken()
      const data = await refreshTokenApi(refreshVal ? { refresh_token: refreshVal } : undefined)

      const newAccessToken = data?.access_token
      const newRefreshToken = data?.refresh_token

      if (!newAccessToken) {
        return { success: false, message: '刷新响应缺少 access token' }
      }

      // Token 轮换：同时更新 access_token 和 refresh_token
      // Web 端：newRefreshToken 为空（已存储在 Cookie 中）
      // App/小程序端：newRefreshToken 有值
      setToken(newAccessToken, newRefreshToken)
      return { success: true, data }
    } catch (error: any) {
      // 如果刷新失败（如 Token 复用检测），清除登录状态
      if (error?.response?.data?.code === 4010 || error?.message?.includes('账号异常')) {
        clearToken()
      }
      return {
        success: false,
        message: error.message || '刷新 token 失败'
      }
    }
  }

  const getUserInfo = async () => {
    try {
      const res = await getCurrentUser()
      if (res.user) setUserInfo(convertUser(res.user))
      return { success: true, data: res }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || '获取用户信息失败'
      }
    }
  }

  const logout = async () => {
    try {
      // 调用后端登出接口，清除 Refresh Token 版本号
      await logoutApi()
    } catch (error) {
      // 即使后端调用失败，也清除本地 Token
      console.error('登出接口调用失败:', error)
    } finally {
      clearToken()
    }
  }

  /**
   * 检查 token 是否需要刷新（即将过期）
   * @param threshold 提前阈值（秒），默认 5 分钟
   * @returns 是否需要刷新
   */
  const shouldRefreshToken = (threshold: number = 300): boolean => {
    const token = accessToken.value
    if (!token) return false

    // 刚获取的 token（10 秒内）不刷新，避免登录后立即刷新
    const now = Math.floor(Date.now() / 1000)
    if (now - tokenIssuedAt.value < 10) {
      return false
    }

    return isTokenExpiringSoon(token, threshold)
  }

  return {
    // State
    accessToken,
    refreshToken,
    token,
    userInfo,
    // Getters
    isLoggedIn,
    userId,
    username,
    avatar,
    role,
    isAdmin,
    // Actions
    login,
    logout,
    getUserInfo,
    setToken,
    clearToken,
    getToken,
    getRefreshToken,
    setUserInfo,
    refreshAccessToken,
    shouldRefreshToken
  }
}, {
  persist: {
    key: USER_INFO_KEY,
    pick: ['accessToken', 'refreshToken', 'userInfo']
  }
})
